// ── A WAY BACK FROM EVERY PRINT WINDOW ──────────────────────────────────────────────────
//
// Kevin, 2026-10-26, on his phone after printing the FSA-156EZ summary: "I need to be able to
// get back into Silas after this print page when on mobile." Every printable sheet in the app
// opens in a NEW window (window.open + document.write, or a Blob URL), and on iOS Safari and the
// home-screen app that window has no back button — history starts at the sheet. The PP-tab PDF
// export carried the same complaint ("no back navigation") for months.
//
// So EVERY print window is written through writePrintWindow() / withPrintBackBar(), which adds
// one fixed bar at the top: "← Back to SILAS" and "Print". tools/check-print-back-bar.js fails
// the build on any document.write or text/html Blob in src/ that does not go through here, so a
// new print page cannot ship without the bar.
//
// ⚠ THE PRINTED SHEET MUST NOT CHANGE. The bar is position:fixed (so it never enters the page's
// flow — a fixed element would otherwise repeat on EVERY printed page) and @media print hides it
// and the screen-only spacer. The spacer is inserted by script AFTER the document's own inline
// scripts have run, so a fit-to-one-page script (fsa156ezPrint.js) measures exactly the layout it
// measured before. The checker prints the 156EZ and the target sheet through Chrome and asserts
// one page and an unchanged fit zoom.
//
// BACK: a script-opened window may close itself, which returns the phone to the SILAS tab.
// Where close is refused or there is no opener (a reloaded tab, a shared link), the window
// navigates to the app instead — never history.back(), which in a fresh window has nowhere to go.
//
// CommonJS, no browser APIs at module scope — App.jsx imports it and the checker requires it.

const BAR_ID = "silas-back-bar";
const MARK = "<!--silas-back-bar-->";

const escAttr = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function appUrlDefault() {
  try { if (typeof window !== "undefined" && window.location && /^https?:/.test(String(window.location.href || ""))) return String(window.location.href); } catch (e) { /* no window */ }
  return "/";
}

// The bar's own markup, style and script. Sizes are in CSS px times --sbb-k, which the script
// sets from the visual viewport so a 44px tap target stays 44 points on a phone even when the
// sheet has no viewport meta and is laid out at desktop width and scaled down.
function backBarHtml(opts) {
  const appUrl = (opts && opts.appUrl) || appUrlDefault();
  return MARK + `
<style id="${BAR_ID}-css">
#${BAR_ID}{position:fixed;top:0;left:0;right:0;z-index:2147483647;--sbb-k:1;display:flex;align-items:center;justify-content:space-between;gap:calc(8px * var(--sbb-k));
  padding:calc(6px * var(--sbb-k)) max(calc(10px * var(--sbb-k)), env(safe-area-inset-right)) calc(6px * var(--sbb-k)) max(calc(10px * var(--sbb-k)), env(safe-area-inset-left));
  padding-top:calc(calc(6px * var(--sbb-k)) + env(safe-area-inset-top));
  background:#1a2e1a;color:#fff;box-shadow:0 1px 4px rgba(0,0,0,.25);font:600 calc(15px * var(--sbb-k))/1.2 -apple-system,"Segoe UI",Roboto,sans-serif;-webkit-text-size-adjust:100%}
#${BAR_ID} button{min-height:calc(44px * var(--sbb-k));min-width:calc(44px * var(--sbb-k));padding:0 calc(16px * var(--sbb-k));border-radius:calc(8px * var(--sbb-k));font:inherit;cursor:pointer;-webkit-tap-highlight-color:transparent}
#${BAR_ID} .sbb-back{background:transparent;color:#fff;border:1.5px solid rgba(255,255,255,.55)}
#${BAR_ID} .sbb-print{background:#fff;color:#1a2e1a;border:1.5px solid #fff}
#${BAR_ID}-spacer{display:block}
@media print{#${BAR_ID},#${BAR_ID}-spacer{display:none !important}}
</style>
<div id="${BAR_ID}" role="navigation" aria-label="SILAS"><button type="button" class="sbb-back" data-app="${escAttr(appUrl)}">&larr; Back to SILAS</button><button type="button" class="sbb-print">Print</button></div>
<script>
(function () {
  var bar = document.getElementById("${BAR_ID}");
  if (!bar) return;
  var app = bar.querySelector(".sbb-back").getAttribute("data-app") || "/";
  function back() {
    try { if (window.opener && !window.opener.closed) { try { window.opener.focus(); } catch (e) {} window.close(); } } catch (e) {}
    setTimeout(function () { if (!window.closed) { try { window.location.replace(app); } catch (e) { window.location.href = app; } } }, 300);
  }
  bar.querySelector(".sbb-back").addEventListener("click", back);
  bar.querySelector(".sbb-print").addEventListener("click", function () { try { window.print(); } catch (e) {} });
  function size() {
    var vv = window.visualViewport, k = vv && vv.scale ? 1 / vv.scale : 1;
    if (!(k > 0) || !isFinite(k)) k = 1;
    k = Math.max(1, Math.min(4, k));
    bar.style.setProperty("--sbb-k", String(k));
    var sp = document.getElementById("${BAR_ID}-spacer");
    if (sp) sp.style.height = bar.offsetHeight + "px";
  }
  // The spacer pushes the sheet below the bar ON SCREEN only (@media print hides it). It is
  // inserted here, after the document's own inline scripts, so a fit script never sees it.
  if (document.body && !document.getElementById("${BAR_ID}-spacer")) {
    var sp = document.createElement("div"); sp.id = "${BAR_ID}-spacer"; sp.setAttribute("aria-hidden", "true");
    document.body.insertBefore(sp, document.body.firstChild);
  }
  size();
  if (window.visualViewport) window.visualViewport.addEventListener("resize", size);
  window.addEventListener("resize", size);
})();
</script>
`;
}

// The sheet with the bar added. Injected just before the LAST </body> so it runs after the
// sheet's own scripts; a document with no </body> gets it appended. Idempotent.
function withPrintBackBar(html, opts) {
  const s = String(html == null ? "" : html);
  if (s.indexOf(MARK) >= 0) return s;
  const bar = backBarHtml(opts);
  const i = s.toLowerCase().lastIndexOf("</body>");
  return i >= 0 ? s.slice(0, i) + bar + s.slice(i) : s + bar;
}

// Write a whole print document into a window the app opened. Replaces whatever the window held
// (a "Building…" placeholder is itself written through here, and is replaced by the sheet).
function writePrintWindow(w, html, opts) {
  if (!w || !w.document) return false;
  // open() is guarded only because the capture stubs in tools/ (check-screens --print,
  // fsr-capture, prf-print-capture) hand back a document with write/close alone.
  if (typeof w.document.open === "function") w.document.open();
  w.document.write(withPrintBackBar(html, opts));
  if (typeof w.document.close === "function") w.document.close();
  return true;
}

module.exports = { withPrintBackBar, writePrintWindow, backBarHtml, BAR_ID, MARK };
